<template>
  <div v-if="billingEntity">
    <ShowPageHeader backLabel="Organisation" :backRoute="organizationRoute" :title="billingEntity.name || '—'">
      <template #actions>
        <SecondaryButton v-if="!billingEntity.mandate" @click="openMandateModal = true">
          Demander un mandat de prelevement
        </SecondaryButton>
        <SecondaryButton v-if="isMandateToSetup(billingEntity.mandate)" @click="openResendModal = true">
          Renvoyer le lien du mandat
        </SecondaryButton>
        <SecondaryButton
          v-if="isMandateToSetup(billingEntity.mandate) || isMandateActive(billingEntity.mandate)"
          @click="openCancelModal = true"
        >
          {{ $t(isMandateActive(billingEntity.mandate) ? "mandate.cancel_button" : "mandate.cancel_request_button") }}
        </SecondaryButton>
      </template>
    </ShowPageHeader>

    <div class="mt-6">
      <BillingEntityGeneralInformation :billing-entity="billingEntity" />
    </div>
    <BillingEntityStoresList :stores="billingEntity.stores || []" />
    <InvoiceMandateSetupModal :open="openMandateModal" @sendEmail="sendMandateForm" @close="openMandateModal = false" />
    <ResendMandateLinkModal
      :open="openResendModal"
      :email="billingEntity.mandate?.responsibleEmail"
      :loading="mandateActionLoading"
      @confirm="resendMandateLink"
      @close="openResendModal = false"
    />
    <CancelMandateModal
      :open="openCancelModal"
      :email="billingEntity.mandate?.responsibleEmail"
      :active="isMandateActive(billingEntity.mandate)"
      :loading="mandateActionLoading"
      @confirm="cancelMandateRequest"
      @close="openCancelModal = false"
    />
  </div>
</template>

<script>
import BillingEntityGeneralInformation from "@/components/billing_entities/BillingEntityGeneralInformation.vue";
import BillingEntityStoresList from "@/components/billing_entities/BillingEntityStoresList.vue";
import InvoiceMandateSetupModal from "@/components/mandate/InvoiceMandateSetupModal.vue";
import CancelMandateModal from "@/components/mandate/CancelMandateModal.vue";
import ResendMandateLinkModal from "@/components/mandate/ResendMandateLinkModal.vue";
import { SecondaryButton, ShowPageHeader } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    BillingEntityGeneralInformation,
    BillingEntityStoresList,
    InvoiceMandateSetupModal,
    CancelMandateModal,
    ResendMandateLinkModal,
    SecondaryButton,
    ShowPageHeader,
  },
  data() {
    return {
      billingEntity: null,
      openMandateModal: false,
      openResendModal: false,
      openCancelModal: false,
      mandateActionLoading: false,
    };
  },
  computed: {
    ...mapGetters("mandates", ["isMandateToSetup", "isMandateActive"]),
    billingEntityId() {
      return this.$route.params.id;
    },
    organizationRoute() {
      return `/organizations/${this.billingEntity?.organization?.id}`;
    },
  },
  async beforeMount() {
    await this.getBillingEntity();
  },
  methods: {
    ...mapActions("billingEntities", ["fetch"]),
    ...mapActions("mandates", ["sendInvoiceMandateForm", "sendNewLink"]),
    ...mapActions("mandates", { cancelMandate: "cancel" }),
    ...mapActions("backoffice", ["withLoader"]),

    async getBillingEntity() {
      await this.withLoader(async () => {
        const response = await this.fetch({
          id: this.billingEntityId,
          includes: ["iban", "mandate", "stores", "organization"],
        });
        this.billingEntity = response.billingEntity;
      });
    },

    async sendMandateForm(email) {
      const { success } = await this.sendInvoiceMandateForm({
        mandateSubjectType: "BillingEntity",
        mandateSubjectId: this.billingEntityId,
        email,
      });
      if (!success) return;
      this.openMandateModal = false;
      await this.getBillingEntity();
    },

    async resendMandateLink() {
      if (this.mandateActionLoading) return;
      this.mandateActionLoading = true;
      try {
        const { success } = await this.sendNewLink({ mandateId: this.billingEntity.mandate.id });
        if (!success) return;
        this.openResendModal = false;
        await this.getBillingEntity();
      } finally {
        this.mandateActionLoading = false;
      }
    },

    async cancelMandateRequest() {
      if (this.mandateActionLoading) return;
      this.mandateActionLoading = true;
      try {
        const { success } = await this.cancelMandate({ mandateId: this.billingEntity.mandate.id });
        if (!success) return;
        this.openCancelModal = false;
        this.openResendModal = false;
        await this.getBillingEntity();
      } finally {
        this.mandateActionLoading = false;
      }
    },
  },
};
</script>
